import React, { ChangeEvent } from 'react';
import styled from 'styled-components';
import { Box, FormControl, InputLabel, ListSubheader, MenuItem, Select } from '@mui/material';
import { SelectChangeEvent } from '@mui/material';
import { truncateField } from '@src/helpers/Helpers';
import CustomTooltip from '../CustomTootip/CustomTooltip';

export interface Option {
	value: string;
	id: string;
	disable?: boolean;
}

type Plan = {
	id: number;
	name: string;
	disable?: boolean;
};

type Status = {
	id: number;
	name: string;
	color: string;
	plans: Plan[];
};

export type StatusList = Status[];

interface LabeledSelectFieldProps {
	label?: string;
	options?: Option[]; //for normal select
	statusOptions?: StatusList; //group select
	value: string;
	error?: boolean;
	disablePortal?: boolean;
	disable?: boolean;
	placeholder?: string;
	defaultValue?: string;
	handleChange: (event: SelectChangeEvent<unknown>) => void;
	width?: string;
	height?: string;
	styles?: React.CSSProperties;
	required?: boolean;
	errorText?: string;
	customTopMessage?: any;
	paperProps?: any;
}

const StyledLabel = styled(Box)`
	color: var(--color-font-grey);
	font-size: 14px;
	font-weight: 600;
	line-height: 15.6px;
	span {
		color: #f26184;
		margin-left: 4px;
	}
`;

const StyledSelect = styled(Select)<{ disabled?: boolean; error?: boolean }>`
	background: var(--others-white, #fff);
	.MuiOutlinedInput-notchedOutline {
		border: 1.5px solid
			${(props) => {
				if (props.error) {
					return 'red';
				} else if (props.disabled) {
					return '#d4d4d4';
				} else {
					return '#e9e9e9';
				}
			}};
	}

	.Mui-focused .MuiOutlinedInput-notchedOutline {
		border: 1.5px solid
			${(props) => {
				if (props.error) {
					return 'red';
				} else if (props.disabled) {
					return '#d4d4d4';
				} else {
					return '#acacac';
				}
			}};
	}
`;

export const StyledMenuItem = styled(MenuItem)`
	color: var(--color-font-grey);
	font-size: 14px;
	padding: 8px 16px;
	z-index: 50;
	width: 100%;

	&:hover {
		background-color: var(--gray-color-light);
	}
`;

const StyledListSubHeader = styled(ListSubheader)<{ name: string }>`
	color: ${(props) =>
		['cancelled'].includes(props?.name)
			? 'red !important'
			: 'var(--default-text-color) !important'};
	font-size: 14px !important;
	font-weight: 700 !important;
	text-transform: Capitalize !important;
`;

const LabeledSelectField: React.FC<LabeledSelectFieldProps> = ({
	label,
	options,
	value,
	error = false,
	disablePortal = true,
	disable,
	handleChange,
	placeholder = '',
	defaultValue = '',
	width = '300px',
	height = '36px',
	styles = { width: '300px', height: '36px' },
	required = false,
	statusOptions,
	errorText,
	customTopMessage = null,
	paperProps,
	...props
}) => {
	const groupSelectStyling = statusOptions?.length
		? {
				width: '0px !important',
				height: '442px !important',
				overflowY: 'auto',
				'& .MuiMenuItem-root': {
					width: styles?.width || 'auto',
				},
				'& .MuiList-root': {
					height: '442px !important',
				},
				'& .MuiListSubheader-root': {
					position: 'static !important',
				},
		  }
		: {
				width: '0px !important',
				maxHeight: '350px !important',
				overflowY: 'auto',
				'& .MuiMenuItem-root': {
					width: styles?.width || 'auto',
				},
		  };
	const customBackdropStyle: React.CSSProperties = {
		backgroundColor: 'transparent',
	};

	const renderSelectGroup = (product: Status) => {
		const items = product?.plans?.map((p) => {
			return (
				<StyledMenuItem
					style={{ paddingLeft: '24px' }}
					key={p?.id}
					value={p?.id}
					disabled={p?.disable}
				>
					{p?.name?.length > 34 ? (
						<CustomTooltip title={p?.name} placement="top">
							<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '290px' }}>
								{p?.name}
							</span>
						</CustomTooltip>
					) : (
						<span>{p?.name}</span>
					)}
				</StyledMenuItem>
			);
		});
		return [
			<StyledListSubHeader name={product?.name?.toLowerCase()}>
				{product?.name?.length > 34 ? (
					<CustomTooltip title={product?.name} placement="top">
						<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '290px' }}>
							{product?.name}
						</span>
					</CustomTooltip>
				) : (
					<span>{product?.name}</span>
				)}
			</StyledListSubHeader>,
			items,
		];
	};

	const menuItem = () => {
		if (options?.length) {
			return options?.map((option) => (
				<StyledMenuItem key={option?.id} value={option?.id} disabled={option?.disable}>
					{option?.disable ? (
						<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
							<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '290px' }}>
								{option?.value}
							</span>
							{!disable ? <span>already assigned</span> : null}
						</div>
					) : option?.value?.length > 34 ? (
						<CustomTooltip title={option?.value} placement="top">
							<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '290px' }}>
								{option?.value}
							</span>
						</CustomTooltip>
					) : (
						<span>{option?.value}</span>
					)}
				</StyledMenuItem>
			));
		} else {
			return statusOptions?.map((p) => renderSelectGroup(p));
		}
	};

	return (
		<Box display="flex" flexDirection="column" gap="5px">
			{label ? (
				<StyledLabel style={disable ? { color: 'var(--gray-g-400, #ACACAC)' } : {}}>
					{label}
					{required ? <span>*</span> : null}
				</StyledLabel>
			) : null}
			<FormControl sx={styles}>
				{value === '' ? (
					<InputLabel
						id="demo-simple-select-helper-label"
						sx={{
							top: '-10px',
						}}
						shrink={false}
					>
						{placeholder}
					</InputLabel>
				) : null}
				<StyledSelect
					value={value || defaultValue}
					onChange={handleChange}
					sx={{ height: styles?.height || '36px' }}
					disabled={disable}
					MenuProps={{
						disablePortal: disablePortal,
						BackdropProps: {
							style: customBackdropStyle,
						},
						PaperProps: {
							//@ts-ignore
							sx: {
								backgroundColor: '#FDFDFD',
								boxShadow: '0px 10px 25px 0px #0000001A',
								...groupSelectStyling,
								...paperProps,
							},
						},
					}}
					{...props}
				>
					{customTopMessage && customTopMessage()}
					{menuItem()}
				</StyledSelect>
				{errorText && errorText !== '' && (
					<div style={{ color: 'red', fontSize: '12px' }}>{errorText}</div>
				)}
			</FormControl>
		</Box>
	);
};

export default LabeledSelectField;
